/**
 * UCM历史记录表格组件
 * 显示UCM报文生成历史记录
 */

import React, { useState } from 'react';
import { Table, Tag, Space, Button, Modal, Typography, message, Popconfirm } from 'antd';
import { EyeOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { UCM_TYPES } from '../constants/ucmConstants';

const { Paragraph, Text } = Typography;

const UcmHistoryTable = ({ 
    dataSource, 
    loading, 
    pagination, 
    onChange,
    onDelete,
}) => {
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // 查看报文详情
    const handleView = (record) => {
        setSelectedRecord(record);
        setViewModalVisible(true);
    };

    // 复制报文
    const handleCopy = (content) => {
        navigator.clipboard.writeText(content).then(() => {
            message.success('报文已复制到剪贴板');
        }).catch(() => {
            message.error('复制失败，请手动复制');
        });
    };

    // 删除记录
    const handleDelete = async (id) => {
        try {
            await onDelete(id);
            message.success('删除成功');
        } catch (error) {
            message.error(error.response?.data?.detail || '删除失败');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            fixed: 'left',
        },
        {
            title: '航班号',
            dataIndex: 'flight_number',
            key: 'flight_number',
            width: 120,
        },
        {
            title: '航班日期',
            dataIndex: 'flight_date',
            key: 'flight_date',
            width: 150,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '起飞机场',
            dataIndex: 'departure_airport',
            key: 'departure_airport',
            width: 100,
        },
        {
            title: '飞机注册号',
            dataIndex: 'aircraft_reg',
            key: 'aircraft_reg',
            width: 130,
        },
        {
            title: '报文类型',
            dataIndex: 'ucm_type',
            key: 'ucm_type',
            width: 110,
            render: (type) => {
                const ucmType = UCM_TYPES[type] || {};
                return (
                    <Tag color={ucmType.color}>
                        {ucmType.label || type}
                    </Tag>
                );
            },
        },
        {
            title: 'ULD数量',
            dataIndex: 'uld_identifiers',
            key: 'uld_count',
            width: 100,
            render: (identifiers) => Array.isArray(identifiers) ? identifiers.length : 0,
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: '创建人',
            dataIndex: 'created_by_user_id',
            key: 'created_by_user_id',
            width: 120,
            render: (userId) => {
                // 简单的用户ID映射（后续可以扩展为从用户服务查询）
                if (userId === '1' || userId === 1) {
                    return <Text>管理员</Text>;
                }
                return <Text>用户 {userId}</Text>;
            },
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 180,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                    >
                        查看
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(record.ucm_content)}
                    >
                        复制
                    </Button>
                    <Popconfirm
                        title="确认删除"
                        description="删除后无法恢复，是否确认删除？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确认"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                onChange={onChange}
                rowKey="id"
                scroll={{ x: 1400 }}
                size="small"
            />

            {/* 查看报文详情弹窗 */}
            <Modal
                title="UCM报文详情"
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                width={700}
                footer={[
                    <Button
                        key="copy"
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(selectedRecord?.ucm_content)}
                    >
                        复制报文
                    </Button>,
                    <Button key="close" onClick={() => setViewModalVisible(false)}>
                        关闭
                    </Button>,
                ]}
            >
                {selectedRecord && (
                    <div>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* 基本信息 */}
                            <div>
                                <Text strong>航班信息：</Text>
                                <div style={{ marginTop: 8 }}>
                                    <Text>航班号：{selectedRecord.flight_number}</Text>
                                    <br />
                                    <Text>航班日期：{dayjs(selectedRecord.flight_date).format('YYYY-MM-DD')}</Text>
                                    <br />
                                    <Text>起飞机场：{selectedRecord.departure_airport}</Text>
                                    <br />
                                    <Text>飞机注册号：{selectedRecord.aircraft_reg}</Text>
                                    <br />
                                    <Text>报文类型：
                                        <Tag color={UCM_TYPES[selectedRecord.ucm_type]?.color} style={{ marginLeft: 8 }}>
                                            {UCM_TYPES[selectedRecord.ucm_type]?.label}
                                        </Tag>
                                    </Text>
                                </div>
                            </div>

                            {/* ULD列表 */}
                            <div>
                                <Text strong>ULD列表（共 {selectedRecord.uld_identifiers?.length || 0} 个）：</Text>
                                <div style={{ 
                                    maxHeight: '150px', 
                                    overflow: 'auto', 
                                    marginTop: 8,
                                    padding: '8px',
                                    background: '#f5f5f5',
                                    borderRadius: '4px',
                                }}>
                                    {selectedRecord.uld_identifiers?.map((uld, index) => (
                                        <Tag key={index} style={{ margin: '4px' }}>{uld}</Tag>
                                    ))}
                                </div>
                            </div>

                            {/* UCM报文内容 */}
                            <div>
                                <Text strong>UCM报文内容：</Text>
                                <div style={{ 
                                    background: '#f5f5f5', 
                                    padding: '16px', 
                                    borderRadius: '4px',
                                    marginTop: 8,
                                }}>
                                    <Paragraph
                                        code
                                        style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            marginBottom: 0,
                                            fontFamily: 'Courier New, monospace',
                                        }}
                                    >
                                        {selectedRecord.ucm_content}
                                    </Paragraph>
                                </div>
                            </div>

                            {/* 元数据 */}
                            <div style={{ fontSize: '12px', color: '#999' }}>
                                <Text>创建时间：{dayjs(selectedRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
                                <br />
                                <Text>创建人：{
                                    selectedRecord.created_by_user_id === '1' || selectedRecord.created_by_user_id === 1
                                        ? '管理员'
                                        : `用户 ${selectedRecord.created_by_user_id}`
                                }</Text>
                            </div>
                        </Space>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default UcmHistoryTable;

